/**
 * AdminHealthAlerts — internal page for publishing WHO/NHS-style
 * global health alerts that appear on Landing + Dashboard banner.
 *
 * Beta MVP scope (per launch plan):
 *   • ONE active alert at a time (server enforces single-active invariant)
 *   • Fields: title, body, severity (info | warning | critical), optional link
 *   • Actions: publish (create active), edit, deactivate, delete
 *   • Mobile-responsive form + history table
 *
 * Out of scope for beta: locale-specific alerts, expiry dates,
 * scheduling, per-user dismissal analytics. Defer to post-beta.
 */
/* eslint-disable */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  ArrowLeft, Megaphone, RefreshCw, Trash2, Power, PowerOff, ExternalLink, Edit3,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_BADGE = {
  info: 'bg-sky-100 text-sky-800 border-sky-200',
  warning: 'bg-amber-100 text-amber-800 border-amber-200',
  critical: 'bg-rose-100 text-rose-800 border-rose-200',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const blankForm = () => ({
  title: '',
  body: '',
  severity: 'info',
  link_url: '',
  link_label: '',
  is_active: true,
});

const AdminHealthAlerts = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  // Defensive role gate.
  useEffect(() => {
    if (user && user.role !== 'admin') navigate('/dashboard');
  }, [user, navigate]);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/health-alerts?limit=50`, { withCredentials: true });
      setItems(res.data?.items || []);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) setError('Please sign in as an admin.');
      else if (status === 403) setError('Admin access required.');
      else setError('Could not load health alerts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts, refreshTick]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title || '',
      body: item.body || '',
      severity: item.severity || 'info',
      link_url: item.link_url || '',
      link_label: item.link_label || '',
      is_active: !!item.is_active,
    });
    // Scroll to form on mobile so the edit area is visible.
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(blankForm());
  };

  const submitForm = async (e) => {
    e?.preventDefault();
    if (!form.title.trim() || !form.body.trim()) {
      toast.error('Title and body are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        body: form.body.trim(),
        severity: form.severity,
        link_url: form.link_url?.trim() || null,
        link_label: form.link_label?.trim() || null,
        is_active: !!form.is_active,
      };
      if (editingId) {
        await axios.patch(`${API}/admin/health-alerts/${editingId}`, payload, { withCredentials: true });
        toast.success('Alert updated');
      } else {
        await axios.post(`${API}/admin/health-alerts`, payload, { withCredentials: true });
        toast.success(payload.is_active ? 'Alert published' : 'Alert saved as inactive');
      }
      setRefreshTick((t) => t + 1);
      cancelEdit();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not save alert.';
      toast.error(typeof msg === 'string' ? msg : 'Could not save alert.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item) => {
    try {
      await axios.patch(
        `${API}/admin/health-alerts/${item.id}`,
        { is_active: !item.is_active },
        { withCredentials: true },
      );
      toast.success(item.is_active ? 'Deactivated' : 'Activated as banner');
      setRefreshTick((t) => t + 1);
    } catch {
      toast.error('Could not change status.');
    }
  };

  const deleteAlert = async (item) => {
    if (typeof window !== 'undefined' && !window.confirm(`Permanently delete "${item.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/health-alerts/${item.id}`, { withCredentials: true });
      toast.success('Deleted');
      setRefreshTick((t) => t + 1);
    } catch {
      toast.error('Could not delete alert.');
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-6">
        <p className="text-sm font-medium text-gray-900 mb-3" data-testid="health-alerts-error">{error}</p>
        <Button variant="outline" onClick={() => navigate('/admin')} data-testid="health-alerts-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to admin
        </Button>
      </div>
    );
  }

  const active = items.find((it) => it.is_active);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-health-alerts-page">
      <header className="bg-white border-b border-gray-100 sticky top-0 md:top-16 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin')}
              aria-label="Back to admin"
              data-testid="back-to-admin-btn"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Megaphone className="h-4 w-4 text-amber-600" />
                <h1 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">Global Health Alerts</h1>
              </div>
              <p className="text-[11px] text-gray-500">WHO / NHS / global advisories shown on Landing + Dashboard</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRefreshTick((t) => t + 1)}
            data-testid="refresh-alerts-btn"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pb-24">
        {/* Currently active summary */}
        <Card className="border border-gray-100 rounded-2xl shadow-sm mb-5" data-testid="active-alert-summary">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Currently shown on Landing + Dashboard</CardTitle>
            <CardDescription className="text-xs">
              Only one alert can be active at a time. Publishing a new active alert automatically deactivates any other.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {active ? (
              <div className="flex items-start gap-3">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide font-bold border ${SEVERITY_BADGE[active.severity] || SEVERITY_BADGE.info}`}
                >
                  {active.severity}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900 text-sm">{active.title}</p>
                  <p className="text-xs text-gray-600 mt-0.5 line-clamp-2 whitespace-pre-line">{active.body}</p>
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-500" data-testid="no-active-alert">No active alert. Users see no banner.</p>
            )}
          </CardContent>
        </Card>

        {/* Form */}
        <Card className="border border-gray-100 rounded-2xl shadow-sm mb-5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              {editingId ? <><Edit3 className="h-4 w-4" /> Edit alert</> : <><Megaphone className="h-4 w-4" /> Publish a new alert</>}
            </CardTitle>
            <CardDescription className="text-xs">
              Keep messages calm and evidence-based. Link to the WHO/NHS source where possible.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={submitForm}>
              <div>
                <Label htmlFor="alert-title" className="text-xs">Title</Label>
                <Input
                  id="alert-title"
                  value={form.title}
                  onChange={(e) => setField('title', e.target.value)}
                  placeholder="E.g. WHO heatwave guidance — stay hydrated and protected"
                  maxLength={160}
                  data-testid="alert-title-input"
                />
              </div>
              <div>
                <Label htmlFor="alert-body" className="text-xs">Message body</Label>
                <Textarea
                  id="alert-body"
                  value={form.body}
                  onChange={(e) => setField('body', e.target.value)}
                  placeholder="Short, calm summary. 1–3 sentences works best."
                  rows={4}
                  maxLength={1200}
                  data-testid="alert-body-input"
                />
                <p className="text-[10px] text-gray-400 mt-1">{form.body.length}/1200 characters</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="alert-severity" className="text-xs">Severity</Label>
                  <Select value={form.severity} onValueChange={(v) => setField('severity', v)}>
                    <SelectTrigger id="alert-severity" data-testid="alert-severity-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="info">Info (blue)</SelectItem>
                      <SelectItem value="warning">Warning (amber)</SelectItem>
                      <SelectItem value="critical">Critical (red)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="alert-link-url" className="text-xs">Link URL (optional)</Label>
                  <Input
                    id="alert-link-url"
                    type="url"
                    value={form.link_url}
                    onChange={(e) => setField('link_url', e.target.value)}
                    placeholder="https://www.who.int/…"
                    data-testid="alert-link-url-input"
                  />
                </div>
                <div>
                  <Label htmlFor="alert-link-label" className="text-xs">Link label</Label>
                  <Input
                    id="alert-link-label"
                    value={form.link_label}
                    onChange={(e) => setField('link_label', e.target.value)}
                    placeholder="Learn more"
                    maxLength={60}
                    data-testid="alert-link-label-input"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!form.is_active}
                  onChange={(e) => setField('is_active', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-emerald-600"
                  data-testid="alert-is-active-checkbox"
                />
                <span className="text-gray-700">
                  Publish as active banner (will deactivate any other active alert)
                </span>
              </label>

              <div className="flex items-center gap-2 pt-1">
                <Button type="submit" disabled={saving} data-testid="alert-submit-btn">
                  {saving ? 'Saving…' : editingId ? 'Save changes' : 'Publish alert'}
                </Button>
                {editingId && (
                  <Button type="button" variant="outline" onClick={cancelEdit} data-testid="alert-cancel-edit-btn">
                    Cancel edit
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {/* History */}
        <Card className="border border-gray-100 rounded-2xl shadow-sm" data-testid="alerts-history-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">History</CardTitle>
            <CardDescription className="text-xs">
              {loading ? 'Loading…' : `${items.length} alert${items.length === 1 ? '' : 's'} published`}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            {items.length === 0 && !loading ? (
              <p className="text-xs text-gray-500">No alerts yet. Publish your first above.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {items.map((item) => (
                  <li
                    key={item.id}
                    className="py-3 flex flex-col sm:flex-row sm:items-start gap-3"
                    data-testid={`alert-row-${item.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide font-bold border ${SEVERITY_BADGE[item.severity] || SEVERITY_BADGE.info}`}
                          data-testid={`alert-severity-${item.id}`}
                        >
                          {item.severity}
                        </span>
                        {item.is_active && (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide font-bold bg-emerald-100 text-emerald-800"
                            data-testid={`alert-active-tag-${item.id}`}
                          >
                            Active
                          </span>
                        )}
                        <span className="text-[11px] text-gray-400">{fmtDate(item.created_at)}</span>
                      </div>
                      <p className="font-medium text-gray-900 text-sm mt-1">{item.title}</p>
                      <p className="text-xs text-gray-600 mt-0.5 whitespace-pre-line line-clamp-2">{item.body}</p>
                      {item.link_url && (
                        <a
                          href={item.link_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[11px] text-emerald-700 hover:underline mt-1 inline-flex items-center gap-1"
                        >
                          {item.link_label || item.link_url} <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => startEdit(item)}
                        data-testid={`alert-edit-btn-${item.id}`}
                      >
                        <Edit3 className="h-3.5 w-3.5 mr-1" /> Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleActive(item)}
                        data-testid={`alert-toggle-btn-${item.id}`}
                      >
                        {item.is_active
                          ? <><PowerOff className="h-3.5 w-3.5 mr-1" /> Deactivate</>
                          : <><Power className="h-3.5 w-3.5 mr-1" /> Activate</>}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteAlert(item)}
                        className="text-rose-600 hover:text-rose-700"
                        data-testid={`alert-delete-btn-${item.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminHealthAlerts;
